/**
 * Branded loading indicator — pure CSS / SVG, no third-party assets, no
 * network fetch on first render. Two concentric rings rotate in opposite
 * directions (spares-rack vibe), the brand-coloured outer arc pulses with
 * a gradient sweep, and the SPARES wordmark below has a per-letter wave
 * animation so the screen never feels frozen.
 *
 * All animations live in this file as a <style> block keyed off a unique
 * class prefix so they don't collide with the rest of the app's CSS, and
 * so dropping this component anywhere is fully self-contained.
 *
 * Variants:
 *   size="lg" — full-screen splash (App boot, Protected gate)
 *   size="md" — page-section loader (Dashboard, list pages)
 *   size="sm" — inline / modal-body loader
 */
export function Loading({
  size = 'md',
  label = 'SPARES',
  centered = true,
  className = '',
  variant = 'default',
}: {
  size?: 'sm' | 'md' | 'lg';
  /** Wordmark below the spinner. Pass empty string to hide. Default 'SPARES'. */
  label?: string;
  /** When true (default), wraps in a flex centered container. Set false to inline. */
  centered?: boolean;
  className?: string;
  /**
   * 'containers' adds a rotating 3D shipping-container model inside the
   * spinner and uses 'IMS' as the default wordmark. Reserved for the IMS
   * Containers module — the Spares system keeps the plain spinner.
   */
  variant?: 'default' | 'containers';
}) {
  const isContainers = variant === 'containers';
  // Default Spares wordmark stays 'SPARES'; containers gets 'IMS' unless
  // the caller passed an explicit label override.
  const resolvedLabel =
    label === 'SPARES' && isContainers ? 'IMS' : label;
  const dim = size === 'lg' ? 140 : size === 'sm' ? 56 : 96;
  const stroke = size === 'lg' ? 6 : size === 'sm' ? 3 : 4;
  const labelCls = size === 'lg'
    ? 'text-2xl font-bold tracking-[0.4em]'
    : size === 'sm'
      ? 'text-[10px] font-semibold tracking-[0.3em]'
      : 'text-sm font-bold tracking-[0.35em]';

  const r = (dim - stroke) / 2;
  const c = dim / 2;
  const circ = 2 * Math.PI * r;
  // Outer ring: 75% solid, 25% gap (creates the chasing arc).
  const outerDash = `${circ * 0.75} ${circ * 0.25}`;
  const innerR = r - stroke - 4;
  const innerCirc = 2 * Math.PI * innerR;
  const innerDash = `${innerCirc * 0.4} ${innerCirc * 0.6}`;

  // Container 3D dims — scale with the loader size. A real shipping
  // container is roughly 2:1:1 (long × tall × deep); we keep the same ratio
  // so the silhouette reads as a container at every variant.
  const cubeW = Math.round(dim * 0.34); // long side
  const cubeH = Math.round(dim * 0.17); // height
  const cubeD = Math.round(dim * 0.17); // depth
  const cubeHalfW = cubeW / 2;
  const cubeHalfH = cubeH / 2;
  const cubeHalfD = cubeD / 2;

  const letters = (resolvedLabel || '').split('');

  const inner = (
    <div className={`spares-loader-root flex flex-col items-center gap-3 ${className}`}>
      <div
        className="spares-loader-spinner relative"
        style={{ width: dim, height: dim }}
      >
        <svg
          width={dim}
          height={dim}
          viewBox={`0 0 ${dim} ${dim}`}
          className="absolute inset-0"
        >
          <defs>
            <linearGradient id={`spares-grad-${dim}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3A56A8" />
              <stop offset="50%" stopColor="#FBC213" />
              <stop offset="100%" stopColor="#FCC93A" />
            </linearGradient>
          </defs>

          {/* Background track */}
          <circle
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke="rgba(148, 163, 184, 0.18)"
            strokeWidth={stroke}
          />
          {/* Outer chasing arc */}
          <circle
            className="spares-loader-outer"
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={`url(#spares-grad-${dim})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={outerDash}
            transform={`rotate(-90 ${c} ${c})`}
          />

          {/* Inner counter-rotating arc */}
          <circle
            cx={c}
            cy={c}
            r={innerR}
            fill="none"
            stroke="rgba(148, 163, 184, 0.12)"
            strokeWidth={Math.max(2, stroke - 2)}
          />
          <circle
            className="spares-loader-inner"
            cx={c}
            cy={c}
            r={innerR}
            fill="none"
            stroke="#3A56A8"
            strokeWidth={Math.max(2, stroke - 2)}
            strokeLinecap="round"
            strokeDasharray={innerDash}
            transform={`rotate(90 ${c} ${c})`}
          />

          {/* Centre dot — pulses to keep the eye anchored. Hidden when
              the 3D container model is active so it doesn't poke through
              the cube. */}
          {!isContainers && (
            <circle
              className="spares-loader-pulse"
              cx={c}
              cy={c}
              r={Math.max(3, stroke)}
              fill="#FBC213"
            />
          )}
        </svg>

        {isContainers && (
          /* 3D shipping container — rotates inside the ring. Pure CSS,
             six face divs assembled into a W:H:D ≈ 2:1:1 cuboid. The
             long walls carry vertical corrugation; one short end is
             marked as the door panel. */
          <div
            className="spares-cube-scene absolute inset-0 flex items-center justify-center"
            aria-hidden
          >
            <div
              className="spares-cube"
              style={{ width: cubeW, height: cubeH }}
            >
              {/* Long-side wall (front) — corrugated */}
              <div
                className="spares-cube-face spares-cube-long"
                style={{ width: cubeW, height: cubeH, transform: `translateZ(${cubeHalfD}px)` }}
              />
              {/* Long-side wall (back) — corrugated */}
              <div
                className="spares-cube-face spares-cube-long"
                style={{ width: cubeW, height: cubeH, transform: `rotateY(180deg) translateZ(${cubeHalfD}px)` }}
              />
              {/* Door end (right) */}
              <div
                className="spares-cube-face spares-cube-door"
                style={{ width: cubeD, height: cubeH, transform: `rotateY(90deg) translateZ(${cubeHalfW}px)` }}
              />
              {/* Short end (left) */}
              <div
                className="spares-cube-face spares-cube-end"
                style={{ width: cubeD, height: cubeH, transform: `rotateY(-90deg) translateZ(${cubeHalfW}px)` }}
              />
              {/* Roof */}
              <div
                className="spares-cube-face spares-cube-top"
                style={{ width: cubeW, height: cubeD, transform: `rotateX(90deg) translateZ(${cubeHalfH}px)` }}
              />
              {/* Floor */}
              <div
                className="spares-cube-face spares-cube-bottom"
                style={{ width: cubeW, height: cubeD, transform: `rotateX(-90deg) translateZ(${cubeHalfH}px)` }}
              />
            </div>
          </div>
        )}
      </div>

      {label && (
        <div className={`spares-loader-label ${labelCls} font-mono select-none`}>
          {letters.map((ch, i) => (
            <span
              key={i}
              className="spares-loader-letter inline-block"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {ch === ' ' ? ' ' : ch}
            </span>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spares-spin       { to { transform: rotate(360deg); } }
        @keyframes spares-spin-back  { to { transform: rotate(-360deg); } }
        @keyframes spares-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.9; }
          50%      { transform: scale(1.6); opacity: 0.4; }
        }
        @keyframes spares-letter-wave {
          0%, 60%, 100% { transform: translateY(0);     color: #4B5563; }
          30%           { transform: translateY(-4px);  color: #FBC213; }
        }
        .spares-loader-outer {
          transform-origin: center;
          animation: spares-spin 1.6s linear infinite;
        }
        .spares-loader-inner {
          transform-origin: center;
          animation: spares-spin-back 2.4s linear infinite;
        }
        .spares-loader-pulse {
          transform-origin: center;
          animation: spares-pulse 1.4s ease-in-out infinite;
        }
        .spares-loader-letter {
          animation: spares-letter-wave 1.6s ease-in-out infinite;
        }

        /* ---- 3D shipping container (IMS Containers variant) ---- */
        @keyframes spares-cube-spin {
          from { transform: rotateX(-18deg) rotateY(0deg); }
          to   { transform: rotateX(-18deg) rotateY(360deg); }
        }
        .spares-cube-scene {
          perspective: 600px;
          perspective-origin: 50% 45%;
        }
        .spares-cube {
          position: relative;
          transform-style: preserve-3d;
          animation: spares-cube-spin 4.5s linear infinite;
          transform: rotateX(-18deg) rotateY(0deg);
        }
        .spares-cube-face {
          position: absolute;
          inset: 0;
          margin: auto;
          box-sizing: border-box;
          border: 1px solid rgba(15, 23, 42, 0.35);
          box-shadow: inset 0 0 6px rgba(15, 23, 42, 0.18);
          backface-visibility: hidden;
        }
        /* Long corrugated walls — indigo/blue with vertical ribs */
        .spares-cube-long {
          background:
            repeating-linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.18) 0px,
              rgba(255, 255, 255, 0.18) 1px,
              rgba(0, 0, 0, 0.18) 1px,
              rgba(0, 0, 0, 0.18) 2px,
              transparent 2px,
              transparent 5px
            ),
            linear-gradient(180deg, #3A56A8 0%, #031A5C 100%);
        }
        /* Door end — vertical split + small handles */
        .spares-cube-door {
          background:
            linear-gradient(90deg,
              transparent 49%,
              rgba(15, 23, 42, 0.55) 49%,
              rgba(15, 23, 42, 0.55) 51%,
              transparent 51%),
            repeating-linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.12) 0px,
              rgba(255, 255, 255, 0.12) 1px,
              rgba(0, 0, 0, 0.18) 1px,
              rgba(0, 0, 0, 0.18) 2px,
              transparent 2px,
              transparent 4px),
            linear-gradient(180deg, #6E86D6 0%, #052379 100%);
        }
        /* Far end — plain corrugated panel */
        .spares-cube-end {
          background:
            repeating-linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.12) 0px,
              rgba(255, 255, 255, 0.12) 1px,
              rgba(0, 0, 0, 0.18) 1px,
              rgba(0, 0, 0, 0.18) 2px,
              transparent 2px,
              transparent 4px),
            linear-gradient(180deg, #6E86D6 0%, #052379 100%);
        }
        /* Roof — lighter, subtle ribs running along length */
        .spares-cube-top {
          background:
            repeating-linear-gradient(
              0deg,
              rgba(255, 255, 255, 0.12) 0px,
              rgba(255, 255, 255, 0.12) 1px,
              rgba(0, 0, 0, 0.12) 1px,
              rgba(0, 0, 0, 0.12) 2px,
              transparent 2px,
              transparent 6px),
            linear-gradient(90deg, #9DB2E2 0%, #6E86D6 100%);
        }
        /* Floor — darker slate */
        .spares-cube-bottom {
          background: linear-gradient(90deg, #010B26 0%, #0B1330 100%);
        }
      `}</style>
    </div>
  );

  if (!centered) return inner;

  const wrapCls = size === 'lg'
    ? 'min-h-[60vh] flex items-center justify-center'
    : size === 'sm'
      ? 'flex items-center justify-center py-3'
      : 'flex items-center justify-center py-10';

  return <div className={wrapCls}>{inner}</div>;
}
